import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserPublicId, getUserRoles } from '@infra/auth';
import { USER_ROLE_CLAIMS } from '@shared/constants';
import { getMeAccess } from './access.api';
import { ACCESS_STALE_EVENT } from './access.events';
import { homePathForAccessRole, roleCan, type AccessCapability } from './roles';
import {
  ORGANIZATION_STATUS,
  meAccessQueryKey,
  type AccessRole,
  type MeAccess,
  type MeOrganization,
  type MeTeam,
} from './access.types';

export interface UseMeAccessResult {
  role: AccessRole;
  organization: MeOrganization | null;
  team: MeTeam | null;
  isLoading: boolean;
  /** The access call failed; `role` is a member fallback, not a confirmed role. */
  isError: boolean;
  retry: () => void;
}

const SUPER_ADMIN_ACCESS: UseMeAccessResult = {
  role: 'super_admin',
  organization: null,
  team: null,
  isLoading: false,
  isError: false,
  retry: () => undefined,
};

const MEMBER_FALLBACK: UseMeAccessResult = {
  role: 'member',
  organization: null,
  team: null,
  isLoading: false,
  isError: false,
  retry: () => undefined,
};

const ACCESS_STALE_MS = 30_000;

/** Super admin is read from the JWT. */
export function isJwtAdmin(): boolean {
  return getUserRoles().includes(USER_ROLE_CLAIMS.ADMIN);
}

/**
 * Who the signed-in person is inside an organization.
 *
 * Super admin is read from the JWT and does not call the API. Everyone else
 * calls GET /v1/me/access. A failed call falls back to member, so a failure
 * never grants org admin.
 */
export function useMeAccess(): UseMeAccessResult {
  const jwtAdmin = isJwtAdmin();
  const userPublicId = getUserPublicId();
  const client = useQueryClient();

  const query = useQuery({
    queryKey: meAccessQueryKey(userPublicId),
    queryFn: (): Promise<MeAccess> => getMeAccess(),
    enabled: !jwtAdmin && Boolean(userPublicId),
    // Short stale time: mount and focus refetch stale data, so an org reactivation
    // reaches the UI without a reload.
    staleTime: ACCESS_STALE_MS,
    retry: 2,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (jwtAdmin) return;
    // Every caller registers this listener, so one event arrives many times.
    // `cancelRefetch: false` lets later calls join the first refetch.
    const onStale = () => {
      void client.invalidateQueries(
        { queryKey: meAccessQueryKey(userPublicId) },
        { cancelRefetch: false }
      );
    };
    window.addEventListener(ACCESS_STALE_EVENT, onStale);
    return () => window.removeEventListener(ACCESS_STALE_EVENT, onStale);
  }, [jwtAdmin, userPublicId, client]);

  if (jwtAdmin) return SUPER_ADMIN_ACCESS;
  if (!userPublicId) return MEMBER_FALLBACK;
  // A confirmed answer survives a failed background refetch.
  if (!query.data) {
    if (query.isError) {
      return { ...MEMBER_FALLBACK, isError: true, retry: () => void query.refetch() };
    }
    return { ...MEMBER_FALLBACK, isLoading: true };
  }

  return {
    role: query.data.role,
    organization: query.data.organization,
    team: query.data.team,
    isLoading: false,
    isError: false,
    retry: () => undefined,
  };
}

/** Re-reads access now, ignoring the stale time. Resolves null when the read fails. */
export function useRefreshMeAccess(): () => Promise<MeAccess | null> {
  const client = useQueryClient();
  const userPublicId = getUserPublicId();
  return useCallback(async () => {
    if (!userPublicId) return null;
    try {
      return await client.fetchQuery({
        queryKey: meAccessQueryKey(userPublicId),
        queryFn: () => getMeAccess(),
        staleTime: 0,
      });
    } catch {
      return null;
    }
  }, [client, userPublicId]);
}

/** True only when the organization is known to be suspended. Unknown stays open. */
export function useOrgPracticeGate(): { blocked: boolean } {
  const { organization } = useMeAccess();
  return { blocked: organization?.status === ORGANIZATION_STATUS.SUSPENDED };
}

/**
 * Home path for the current person. `null` while access is still loading, so a
 * redirect waits for the real role instead of guessing from the cache.
 */
export function useHomePath(): string | null {
  const { role, isLoading } = useMeAccess();
  if (isLoading) return null;
  return homePathForAccessRole(role);
}

/** Whether the current person's role grants a capability. False while access is loading. */
export function useCan(capability: AccessCapability): boolean {
  const { role, isLoading } = useMeAccess();
  return !isLoading && roleCan(role, capability);
}
